/**
 * Client-side InfluxDB service — thin JSON fetchers only.
 *
 * All Flux queries, CSV parsing, and data transformation have been moved to
 * server/routes/influxData.js. Each function here does a single authenticated
 * GET request and returns the pre-formatted JSON object from the server.
 */

import { apiFetch } from './api.js'

const BASE = (import.meta.env.VITE_API_BASE ?? '') + '/influx/data'

async function get(path) {
  const r = await apiFetch(`${BASE}${path}`)
  if (!r.ok) throw new Error(`InfluxDB data error: ${r.status}`)
  return r.json()
}

// ── Merge multi-series into [{time, ...seriesKeys}] for Recharts ──────────────
// Kept client-side: it's a lightweight object merge used by ChartsCard, not
// string-heavy CSV parsing.
export function mergeForRecharts(multiSeries) {
  const timeMap = {}
  for (const [key, points] of Object.entries(multiSeries)) {
    for (const { time, value } of points) {
      if (!timeMap[time]) timeMap[time] = { time }
      timeMap[time][key] = value
    }
  }
  return Object.values(timeMap).sort((a, b) => a.time - b.time)
}

// ── Polled by DataContext ─────────────────────────────────────────────────────

export const fetchBatteryData         = () => get('/battery')
export const fetchBatteryTodayStats   = () => get('/battery-today')
export const fetchTodayEnergyStats    = () => get('/today-energy')
export const fetchEnergyFlowData      = () => get('/energy-flow')
export const fetchInverterSparklines  = () => get('/inverter-sparklines')
export const fetchInverterLastValues  = () => get('/inverter-last')
export const fetchShellyLastValues    = () => get('/shelly-last')
export const fetchShellyToday         = () => get('/shelly-today')
export const fetchSolarPrediction          = (start, end) => get(`/solar-prediction${start && end ? `?start=${start}&end=${end}` : ''}`)
export const fetchSolarPredictionInverters = () => get('/solar-prediction-inverters')

// ── Range-parameterised (charts) ──────────────────────────────────────────────

export const fetchTadoTemperatures  = (range = '-24h') => get(`/tado-temperatures?range=${encodeURIComponent(range)}`)
export const fetchSolarPower        = (range = '-24h') => get(`/solar-power?range=${encodeURIComponent(range)}`)
export const fetchEnergyOverview    = (range = '-24h') => get(`/energy-overview?range=${encodeURIComponent(range)}`)
export const fetchBatteryBoxPower   = (range = '-24h') => get(`/battery-box-power?range=${encodeURIComponent(range)}`)
export const fetchShellyPower       = (range = '-24h') => get(`/shelly-power?range=${encodeURIComponent(range)}`)

// ── Historical (start / stop params) ─────────────────────────────────────────

export const fetchShellyHistorical          = (start, stop = 'now()') => get(`/shelly-historical?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
export const fetchEnergyHistorical          = (start, stop = 'now()') => get(`/energy-historical?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
export const fetchConsumptionHistorical     = (start, stop = 'now()') => get(`/consumption-historical?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
export const fetchSelfSufficiencyHistorical = (start, stop = 'now()') => get(`/self-sufficiency-historical?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
export const fetchRoiHistorical             = (start, stop = 'now()') => get(`/roi-historical?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
export const fetchTariffSplitHistorical     = (start, stop = 'now()') => get(`/tariff-split-historical?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
export const fetchCarChargeHistorical       = (start, stop = 'now()') => get(`/car-charge-historical?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
export const fetchCarChargeCompare          = () => get('/car-charge-compare')
export const fetchCarChargeSummary          = () => get('/car-charge-summary')
export const fetchConsumptionBreakdown      = () => get('/consumption-breakdown')
export const fetchConsumptionLive          = () => get('/consumption-live')
export const fetchApplianceAdvice          = () => get('/appliance-advice')
export const fetchBatterySummary            = () => get('/battery-summary')
export const fetchBatteryPrice              = () => get('/battery-price')
export const fetchCarChargeKpis             = (start, stop = 'now()') => get(`/car-charge-kpis?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`)
